import { useState } from "react";
import { confirmPasswordReset } from "../../services/authService";
import {
  resetPasswordRequirementsMessage,
  validatePasswordValues,
} from "../../utils/passwordValidation";
import { BrandField } from "../form/BrandField";
import { BrandButton } from "../ui/BrandButton";
import { FormCard } from "../form/FormCard";
import { brandFormCardClass } from "../form/fieldStyles";

const initialValues = {
  password: "",
  passwordConfirm: "",
};

export function ResetPasswordForm({ token, logoSrc, onSuccess }) {
  const [values, setValues] = useState(initialValues);
  const [touched, setTouched] = useState({});
  const [fieldErrors, setFieldErrors] = useState({});
  const [submitError, setSubmitError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const showError = (field) => touched[field] && fieldErrors[field];

  const handleChange = (event) => {
    const { name, value } = event.target;
    setValues((current) => ({
      ...current,
      [name]: value,
    }));
    setSubmitError("");
    setFieldErrors((current) => {
      if (!current[name]) return current;
      const next = { ...current };
      delete next[name];
      return next;
    });
  };

  const handleBlur = (event) => {
    const { name } = event.target;
    setTouched((current) => ({ ...current, [name]: true }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const errors = validatePasswordValues(values);
    setTouched({
      password: true,
      passwordConfirm: true,
    });

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    setIsSubmitting(true);
    setSubmitError("");
    setFieldErrors({});

    try {
      const response = await confirmPasswordReset({
        token,
        password: values.password,
        passwordConfirm: values.passwordConfirm,
      });

      onSuccess?.(response.message);
    } catch (error) {
      if (error.code === "TOO_MANY_REQUESTS") {
        setSubmitError(error.message);
        return;
      }

      if (error.code === "RESET_TOKEN_EXPIRED" || error.code === "RESET_TOKEN_USED" || error.code === "RESET_TOKEN_REVOKED" || error.code === "INVALID_RESET_TOKEN") {
        setSubmitError(error.message);
        return;
      }

      if (Array.isArray(error.details)) {
        const mappedErrors = error.details.reduce((acc, item) => {
          if (item?.path) {
            acc[item.path] = item.message;
          }
          return acc;
        }, {});

        if (Object.keys(mappedErrors).length > 0) {
          setFieldErrors(mappedErrors);
          return;
        }
      }

      setSubmitError(error.message || "No pudimos cambiar la contraseña.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <FormCard className={brandFormCardClass}>
      <div className="mb-6 flex flex-col items-center text-center">
        <h2 className="mt-5 font-display text-3xl text-brand-primary sm:text-4xl">
          Crear nueva contraseña
        </h2>
        <p className="mt-3 max-w-2xl text-sm leading-6 text-slate-600 sm:text-base">
          Elegí una contraseña nueva para tu cuenta. El enlace solo puede
          usarse una vez.
        </p>
      </div>

      {submitError ? (
        <div className="mb-5 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          {submitError}
        </div>
      ) : null}

      <form className="grid gap-4" onSubmit={handleSubmit} noValidate>
        <BrandField
          label="Nueva contraseña"
          name="password"
          value={values.password}
          onChange={handleChange}
          onBlur={handleBlur}
          error={showError("password") || fieldErrors.password}
          helper={resetPasswordRequirementsMessage}
          required
          type={showPassword ? "text" : "password"}
          actionLabel={showPassword ? "Ocultar" : "Mostrar"}
          onActionClick={() => setShowPassword((current) => !current)}
          autoComplete="new-password"
        />

        <BrandField
          label="Repetir contraseña"
          name="passwordConfirm"
          value={values.passwordConfirm}
          onChange={handleChange}
          onBlur={handleBlur}
          error={showError("passwordConfirm") || fieldErrors.passwordConfirm}
          required
          type={showPassword ? "text" : "password"}
          actionLabel={showPassword ? "Ocultar" : "Mostrar"}
          onActionClick={() => setShowPassword((current) => !current)}
          autoComplete="new-password"
        />

        <BrandButton
          type="submit"
          disabled={isSubmitting}
          variant="primary"
          radius="2xl"
          size="lg"
          className="w-full"
        >
          {isSubmitting ? "Guardando..." : "Cambiar contraseña"}
        </BrandButton>
      </form>
    </FormCard>
  );
}
